<template>
  <div class="lg:hidden mb-6">
    <div class="flex gap-4">
      <Button @click="showFilters = true" size="lg" variant="outline" type="button" class="flex-1">
        Filter
        <Filter />
      </Button>
      <Button @click="showSort = true" size="lg" variant="outline" type="button" class="flex-1">
        Sort by
        <ChevronUp />
      </Button>
    </div>

    <Dialog v-model:open="showFilters">
      <DialogContent class="max-h-[90vh] flex flex-col">
        <DialogHeader class="shrink-0">
          <DialogTitle>
            <h4 class="text-grey-light-900 font-medium text-xl">Filters</h4>
            <DialogDescription class="sr-only">Filters for store directory</DialogDescription>
          </DialogTitle>
        </DialogHeader>
        <div class="flex-1 overflow-y-auto space-y-2 py-2">
          <AccordionSection title="Categories">
            <div v-for="(cat, i) in filterCategories" :key="i" class="flex items-center space-x-3">
              <Checkbox :id="`m-cat${i}`" v-model="cat.selected" />
              <label :for="`m-cat${i}`" class="leading-none cursor-pointer text-grey-light-700">{{ cat.name }}</label>
            </div>
          </AccordionSection>
          <AccordionSection title="Type">
            <div v-for="(type, i) in filterTypes" :key="i" class="flex items-center space-x-3">
              <Checkbox :id="`m-type${i}`" v-model="type.selected" />
              <label :for="`m-type${i}`" class="leading-none cursor-pointer text-grey-light-700">{{ type.name }}</label>
            </div>
          </AccordionSection>
          <AccordionSection
            v-if="shopsStore.currentProduct === 'layby' || shopsStore.currentProduct === 'plans'"
            :title="shopsStore.currentProduct === 'layby' ? 'Lay-by Duration' : 'Plan Duration'"
          >
            <div v-for="(dur, i) in filterDurations" :key="i" class="flex items-center space-x-3">
              <Checkbox :id="`m-dur${i}`" v-model="dur.selected" />
              <label :for="`m-dur${i}`" class="leading-none cursor-pointer text-grey-light-700">{{ dur.name }}</label>
            </div>
          </AccordionSection>
          <AccordionSection v-if="shopsStore.currentProduct === 'boost'" title="Reward Percentages">
            <div v-for="(perc, i) in filterRewardPercentages" :key="i" class="flex items-center space-x-3">
              <Checkbox :id="`m-perc${i}`" v-model="perc.selected" />
              <label :for="`m-perc${i}`" class="leading-none cursor-pointer text-grey-light-700">{{ perc.name }}</label>
            </div>
          </AccordionSection>
          <AccordionSection v-if="shopsStore.currentProduct === 'boost'" title="Minimum Savings Amount">
            <div v-for="(amt, i) in filterMinimumAmounts" :key="i" class="flex items-center space-x-3">
              <Checkbox :id="`m-amt${i}`" v-model="amt.selected" />
              <label :for="`m-amt${i}`" class="leading-none cursor-pointer text-grey-light-700">{{ amt.name }}</label>
            </div>
          </AccordionSection>
        </div>
        <div class="flex flex-col gap-2 shrink-0 pt-4">
          <Button type="button" class="rounded-full text-base" @click="showFilters = false">Apply</Button>
          <Button type="button" variant="ghost" class="rounded-full text-base" @click="shopsStore.clearFilters"
            >Clear All</Button
          >
        </div>
      </DialogContent>
    </Dialog>

    <Dialog v-model:open="showSort">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <div class="flex justify-between items-center">
              <h4 class="text-grey-light-900 font-medium text-xl">Sort by</h4>
              <Button type="button" variant="ghost" @click="showSort = false"><XIcon /></Button>
            </div>
          </DialogTitle>
          <DialogDescription class="sr-only">Sort options for store directory</DialogDescription>
        </DialogHeader>
        <div class="flex flex-col gap-2 py-4">
          <div
            v-for="opt in sortOptions"
            :key="opt.value"
            @click="
              shopsStore.setSortBy(opt.value);
              showSort = false;
            "
            class="text-grey-light-900 text-lg font-medium hover:bg-grey-light-50 p-3 rounded-lg cursor-pointer transition-colors"
          >
            {{ opt.label }}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useShopsStore } from '@/stores/shops';
import { ChevronUp, Filter, Minus, Plus, XIcon } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { defineComponent, h, ref } from 'vue';

const shopsStore = useShopsStore();
const { filterCategories, filterTypes, filterDurations, filterRewardPercentages, filterMinimumAmounts } =
  storeToRefs(shopsStore);

const showFilters = ref(false);
const showSort = ref(false);

const sortOptions = [
  { label: 'A-Z', value: 'a-z' as const },
  { label: 'Z-A', value: 'z-a' as const },
  { label: 'Newest', value: 'newest' as const },
];

const AccordionSection = defineComponent({
  props: { title: { type: String, required: true } },
  setup(props, { slots }) {
    const open = ref(false);
    return () =>
      h('div', { class: 'flex flex-col gap-2' }, [
        h(
          'div',
          {
            class:
              'flex justify-between items-center p-2 pl-4 text-lg font-medium border border-grey-light-200 rounded-lg text-grey-light-900 cursor-pointer',
            onClick: () => (open.value = !open.value),
          },
          [props.title, h(open.value ? Minus : Plus, { class: 'text-grey-light-900' })],
        ),
        open.value
          ? h('div', { class: 'p-4 space-y-6 border border-grey-light-200 rounded-lg' }, slots.default?.())
          : null,
      ]);
  },
});
</script>
